import { snakeToCamel } from '@utils/format.utils'

import { InitOrCreatePspToWalletDepositTransactionCommand } from './InitOrCreatePspToWalletDepositTransactionCommand'
import {
  FormItemDto,
  isFormItemInputDto,
  isFormItemSelectDto,
} from './WalletPaymentProviderParametersDto'

const REQUIRED_THREE_DS_DETAIL_NAMES = new Set([
  'three_ds_card_enrollment_code',
  'three_ds_authentication_value',
  'three_ds_eci',
  'three_ds_transaction_id',
  'three_ds_status_code',
  'three_ds_secure_flow',
  'three_ds_session_id',
])

export interface CreatePspToWalletDepositTransaction {
  fromPaymentProvider: Wallet
  toWallet: Wallet
  tradingAccount?: TradingAccount
  amount: number | undefined
  comment: string
  terms: boolean
  fields?: { [key: string]: string }
  form: FormItemDto[]
  bonusCampaignId?: string
}

interface Wallet {
  id: string | undefined
  currency: { id: string | undefined }
}

interface TradingAccount {
  id: string | undefined
  campaign: {
    id: string | undefined
  }
}

export const denormalizeCreatePspToWalletDepositTransaction = (
  values: CreatePspToWalletDepositTransaction,
  locale: string
): Omit<InitOrCreatePspToWalletDepositTransactionCommand, 'caller' | 'clientIpAddress'> => {
  return {
    languageId: locale,
    fromPspId: getFromPspId(values),
    toWalletId: getToWallet(values),
    pspCurrencyId: getCurrencyValue(values),
    amount: getAmount(values),
    comment: getComment(values),
    details: getDetails(values),
    bonusCampaignId: getBonusDepositCampaignId(values),
  }
}

const getAmount = (values: CreatePspToWalletDepositTransaction): string => {
  return values.amount?.toString() || '0'
}

const getFromPspId = (values: CreatePspToWalletDepositTransaction): string => {
  return values.fromPaymentProvider.id || ''
}

const getToWallet = (values: CreatePspToWalletDepositTransaction): string => {
  return values.toWallet.id || ''
}

const getComment = (values: CreatePspToWalletDepositTransaction): string => {
  return values.comment || ''
}

const getTerms = (values: CreatePspToWalletDepositTransaction): string => {
  return values.terms ? '1' : '0'
}

const getTradingAccountId = (values: CreatePspToWalletDepositTransaction): string => {
  return values?.tradingAccount?.id || ''
}

const getTradingAccountCampaignId = (values: CreatePspToWalletDepositTransaction): string => {
  return values?.tradingAccount?.campaign?.id || ''
}

const getBonusDepositCampaignId = (values: CreatePspToWalletDepositTransaction): string => {
  return values?.bonusCampaignId || ''
}

const getCurrencyValue = (values: CreatePspToWalletDepositTransaction): string => {
  const paymentCurrencyField = 'payment_currency_id'
  const paymentCurrencyForm = (values.form || []).find((x) => x.field === paymentCurrencyField)

  if (isFormItemSelectDto(paymentCurrencyForm)) {
    return values.fromPaymentProvider.currency.id || ''
  }

  if (isFormItemInputDto(paymentCurrencyForm)) {
    return paymentCurrencyForm.value
  }

  return values.toWallet.currency.id || ''
}

const getDetails = (
  values: CreatePspToWalletDepositTransaction
): { name: string; value: string }[] => {
  const formFieldNameMap = (values.form || []).reduce<Record<string, string>>((acc, item) => {
    const detailName = item.grouping ? `${item.grouping}_${item.field}` : item.field
    acc[snakeToCamel(detailName)] = detailName
    return acc
  }, {})

  const allDetails = [
    { name: 'amount', value: getAmount(values) },
    { name: 'additionalComment', value: getComment(values) },
    { name: 'terms', value: getTerms(values) },
    { name: 'tradingAccountId', value: getTradingAccountId(values) },
    { name: 'campaignId', value: getTradingAccountCampaignId(values) },
    ...Object.entries(values?.fields || []).map(([name, value]) => ({
      name: formFieldNameMap[name] ?? name,
      value,
    })),
  ]

  const uniqueDetailsMap = new Map<string, { name: string; value: string }>()
  allDetails.forEach((detail) => {
    const existing = uniqueDetailsMap.get(detail.name)
    if (!existing || detail.value) {
      uniqueDetailsMap.set(detail.name, detail)
    }
  })

  return Array.from(uniqueDetailsMap.values()).filter(
    (x) => !!x.value || REQUIRED_THREE_DS_DETAIL_NAMES.has(x.name)
  )
}
